import {
  exampleWorkspaceHelpThreadRouteId,
  isExampleHelpAccessActive,
  // Relative, not `@/convex`, so the native app (where `@/` is apps/ios/src)
  // shares this rule with web (#6174).
} from '../../convex/auth/example_threads/route_ids';

const FREE_YOUR_IMAI_SEEN_STORAGE_KEY_PREFIX = 'imai.free-your-imai-seen';

type FreeWorkspacePlan = string | null | undefined;

export function isFreeWorkspacePlan(plan: FreeWorkspacePlan): boolean {
  return plan === 'free';
}

export function getWorkspaceSidebarFooterMode(
  plan: FreeWorkspacePlan
): 'free-upgrade' | 'support-feedback' {
  return isFreeWorkspacePlan(plan) ? 'free-upgrade' : 'support-feedback';
}

export function shouldShowFreeWorkspacePricing(
  plan: FreeWorkspacePlan
): boolean {
  return isFreeWorkspacePlan(plan);
}

export function freeWorkspaceYourImaiSeenStorageKey(
  organizationId: string
): string {
  return `${FREE_YOUR_IMAI_SEEN_STORAGE_KEY_PREFIX}:${organizationId}`;
}

export function hasSeenFreeWorkspaceYourImai(organizationId: string): boolean {
  try {
    return (
      window.localStorage.getItem(
        freeWorkspaceYourImaiSeenStorageKey(organizationId)
      ) === '1'
    );
  } catch {
    return false;
  }
}

export function markFreeWorkspaceYourImaiSeen(organizationId: string): void {
  try {
    window.localStorage.setItem(
      freeWorkspaceYourImaiSeenStorageKey(organizationId),
      '1'
    );
  } catch {
    // Storage can be unavailable in private browsing or hardened contexts.
  }
}

/**
 * Whether the free workspace window is open: free plan, no credits and a
 * workspace younger than 24 hours. Mirrors the server's sponsored free lane
 * apart from the owner check.
 */
function isFreeWorkspaceWindowOpen(args: {
  billingPlan: FreeWorkspacePlan;
  creditBalance: number | null | undefined;
  workspaceCreatedAt: number | null | undefined;
  now?: number;
}): boolean {
  return (
    isFreeWorkspacePlan(args.billingPlan) &&
    typeof args.creditBalance === 'number' &&
    args.creditBalance <= 0 &&
    typeof args.workspaceCreatedAt === 'number' &&
    isExampleHelpAccessActive(args.workspaceCreatedAt, args.now)
  );
}

/** `/dashboard` opens Your IMAI once for a new free workspace with no credits. */
export function shouldRedirectToFreeWorkspaceYourImai(args: {
  billingPlan: FreeWorkspacePlan;
  creditBalance: number | null | undefined;
  organizationId: string | null | undefined;
  workspaceCreatedAt: number | null | undefined;
  yourImaiSeen: boolean;
  now?: number;
}): boolean {
  return (
    Boolean(args.organizationId) &&
    isFreeWorkspaceWindowOpen(args) &&
    !args.yourImaiSeen
  );
}

export function isSeededHelpThreadRoute(args: {
  routeId: string | null | undefined;
  organizationId: string | null | undefined;
}): boolean {
  if (!args.routeId || !args.organizationId) return false;
  return (
    args.routeId === exampleWorkspaceHelpThreadRouteId(args.organizationId)
  );
}

/**
 * Whether a credit-blocked text can move to Your IMAI as a draft: only the
 * workspace owner gets sponsored free messages there.
 */
export function canRedirectBlockedTextToYourImai(args: {
  billingPlan: FreeWorkspacePlan;
  creditBalance: number | null | undefined;
  workspaceCreatedAt: number | null | undefined;
  isWorkspaceOwner: boolean;
  now?: number;
}): boolean {
  return args.isWorkspaceOwner && isFreeWorkspaceWindowOpen(args);
}
